import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { X, FolderPlus, ArrowRight, Sparkles, Wand2, CheckCircle2 } from 'lucide-react';

/**
 * SaveToCollectionModal — the "Save to Collection" action used across the app.
 *
 * Modes:
 *   - Single save (default): pass `listing` — one item saved.
 *   - Bulk save: pass `listings=[...]` — multiple saved via /save-bulk.
 *
 * Features (Single-Pass Completion Directive, Feb 2026):
 *   - Choose existing folder OR name a new one inline.
 *   - Ari auto-categorization (Wand2 button): calls /suggest-folder, then
 *     asks the user to confirm before creating any new nested folder.
 *   - Search-term / scan_id metadata preserved on every saved item so the
 *     item's provenance survives exports.
 *   - Paywall fallback if the user lacks the $9.99/mo add-on.
 */
export const SaveToCollectionModal = ({
  listing,
  listings,
  searchTerm,
  scanId,
  onClose,
  onSaved,
}) => {
  const bulkMode = Array.isArray(listings) && listings.length > 0;
  const first = bulkMode ? listings[0] : listing;

  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState('');
  const [newName, setNewName] = useState('');
  const [parentId, setParentId] = useState(null);
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);
  const [needsAddon, setNeedsAddon] = useState(false);
  const [loading, setLoading] = useState(true);

  // Ari auto-categorization suggestion
  const [suggestion, setSuggestion] = useState(null); // {path, is_new_parent, is_new_child, ...}
  const [suggestBusy, setSuggestBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get('/collections');
        if (!mounted) return;
        setCollections(data.collections || []);
        if (data.collections?.length) setSelected(data.collections[0].id);
        setLoading(false);
      } catch (e) {
        if (e.response?.status === 402) {
          setNeedsAddon(true);
        } else {
          toast.error('Could not load your collections.');
        }
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const askAri = async () => {
    if (!first) return;
    setSuggestBusy(true);
    try {
      const { data } = await api.post('/collections/suggest-folder', {
        title: first.title,
        description: first.description || null,
        source_name: first.source_name || null,
        category: first.category || null,
        external_url: first.external_url || null,
        search_term: searchTerm || null,
      });
      setSuggestion(data);
    } catch (e) {
      toast.error('Ari could not suggest a folder right now.');
    } finally {
      setSuggestBusy(false);
    }
  };

  const acceptSuggestion = async () => {
    if (!suggestion) return;
    // If both parent and child exist → use the child (or parent if only 1 element)
    if (!suggestion.is_new_parent && !suggestion.is_new_child) {
      const targetId = suggestion.child_id || suggestion.parent_id;
      setSelected(targetId || '');
      setNewName('');
      setParentId(null);
      toast.success(`Using existing folder: ${suggestion.path.join(' › ')}`);
      setSuggestion(null);
      return;
    }
    // Create whichever pieces are missing (with user confirmation via the button click)
    try {
      let parentIdResolved = suggestion.parent_id;
      if (suggestion.is_new_parent) {
        const { data } = await api.post('/collections', {
          name: suggestion.path[0],
          purpose: suggestion.purpose_hint || null,
        });
        parentIdResolved = data.id;
      }
      let finalId = parentIdResolved;
      if (suggestion.path.length > 1 && suggestion.is_new_child) {
        const { data } = await api.post('/collections', {
          name: suggestion.path[1],
          parent_id: parentIdResolved,
          purpose: suggestion.purpose_hint || null,
        });
        finalId = data.id;
      } else if (suggestion.path.length > 1 && suggestion.child_id) {
        finalId = suggestion.child_id;
      }
      // Reload collections so the selects show the new folder
      const { data } = await api.get('/collections');
      setCollections(data.collections || []);
      setSelected(finalId);
      setNewName('');
      setParentId(null);
      toast.success(`Folder ready: ${suggestion.path.join(' › ')}`);
      setSuggestion(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create folder.');
    }
  };

  const rejectSuggestion = () => setSuggestion(null);

  const listingToPayloadItem = (l) => ({
    title: l.title,
    external_url: l.external_url,
    price: l.price,
    is_free: l.is_free,
    location_text: l.location_text,
    distance_miles: l.distance_miles,
    source_key: l.source_key,
    source_name: l.source_name,
    photos: l.photos,
    description: l.description,
    date_found: l.fetched_at || l.posted_at,
    scan_id: l.scan_id || scanId,
    listing_id: l.id && l.scan_id !== undefined ? l.id : undefined,
    posted_at: l.posted_at,
  });

  const save = async () => {
    if (!first) return;
    if (!selected && !newName.trim()) {
      toast.error('Choose a collection or name a new one.');
      return;
    }
    setBusy(true);
    try {
      const tagList = tags.split(',').map((t) => t.trim()).filter(Boolean);

      if (bulkMode) {
        // Split: those with `id` + `scan_id` → listing_ids; the rest → raw items
        const listingIds = [];
        const rawItems = [];
        for (const l of listings) {
          if (l.id && l.scan_id !== undefined) listingIds.push(l.id);
          else rawItems.push(listingToPayloadItem(l));
        }
        const payload = {
          collection_id: selected || null,
          collection_name: !selected ? newName.trim() : null,
          parent_id: !selected ? parentId : null,
          listing_ids: listingIds,
          items: rawItems,
          tags: tagList,
          search_term: searchTerm || null,
          scan_id: scanId || null,
        };
        const { data } = await api.post('/collections/save-bulk', payload);
        toast.success(
          `Saved ${data.saved} to collection${data.skipped_duplicates ? ` · ${data.skipped_duplicates} already there` : ''}.`,
        );
        onSaved && onSaved(data);
        onClose && onClose();
        return;
      }

      // Single save
      const payload = {
        collection_id: selected || null,
        collection_name: !selected ? newName.trim() : null,
        parent_id: !selected ? parentId : null,
        tags: tagList,
        notes: notes.trim() || null,
        search_term: searchTerm || null,
        scan_id: scanId || first.scan_id || null,
      };
      if (first.id && first.scan_id !== undefined) {
        payload.listing_id = first.id;
      } else {
        payload.item = listingToPayloadItem(first);
      }
      const { data } = await api.post('/collections/save', payload);
      if (data.status === 'already_saved') {
        toast.success('Already in that collection.');
      } else {
        toast.success('Saved to collection.');
      }
      onSaved && onSaved(data);
      onClose && onClose();
    } catch (e) {
      if (e.response?.status === 402) setNeedsAddon(true);
      else toast.error(e.response?.data?.detail || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const paywall = (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-full zita-ari-avatar flex items-center justify-center">
          <Sparkles size={16} />
        </div>
        <div className="text-xs uppercase tracking-[0.16em] zita-ink-muted">Zita Pro required</div>
      </div>
      <h2 className="font-serif text-2xl zita-ink">Organize what Ari finds — start your Zita Pro trial.</h2>
      <p className="mt-2 text-sm zita-ink-soft leading-relaxed">
        Collections is part of Zita Pro at $29.99 / month. Start your 3-day
        free trial to save opportunities, organize into unlimited folders,
        keep search context frozen, and export as Excel, CSV, PDF, or Google
        Sheets.
      </p>
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <a
          href="/subscribe"
          className="zita-btn-primary text-sm inline-flex items-center gap-2"
          data-testid="save-modal-upgrade"
        >
          Start Zita Pro <ArrowRight size={14} />
        </a>
        <button type="button" onClick={onClose} className="zita-btn-secondary text-sm">Not now</button>
      </div>
    </div>
  );

  const form = (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full zita-ari-avatar flex items-center justify-center">
            <FolderPlus size={16} />
          </div>
          <div className="text-xs uppercase tracking-[0.16em] zita-ink-muted">
            {bulkMode ? `Save ${listings.length} matches` : 'Save to collection'}
          </div>
        </div>
        {!bulkMode && first && (
          <button
            type="button"
            onClick={askAri}
            disabled={suggestBusy}
            data-testid="save-modal-suggest"
            className="zita-btn-ari text-xs inline-flex items-center gap-1 !px-3 !py-1.5"
            title="Let Ari suggest a folder"
          >
            <Wand2 size={12} /> {suggestBusy ? 'Thinking…' : 'Ari suggest'}
          </button>
        )}
      </div>
      <h2 className="font-serif text-2xl zita-ink truncate">
        {bulkMode ? `${listings.length} opportunities` : (first?.title || 'Save opportunity')}
      </h2>
      <p className="mt-1 text-xs zita-ink-muted">
        Zita keeps the original source link, search context, and every available field.
      </p>

      {suggestion && (
        <div className="mt-4 zita-card p-3 border border-[color:var(--zita-blue-soft)] bg-[color:var(--zita-blue-soft)]/40">
          <div className="flex items-start gap-2">
            <Sparkles size={16} className="mt-0.5 ari-accent shrink-0" />
            <div className="flex-1">
              <div className="text-sm zita-ink font-semibold">
                Ari suggests: {suggestion.path.join(' › ')}
                {suggestion.is_new_parent || suggestion.is_new_child ? (
                  <span className="ml-1 text-[10px] font-normal ari-accent">(new folder)</span>
                ) : (
                  <span className="ml-1 text-[10px] font-normal zita-ink-muted">(existing)</span>
                )}
              </div>
              {suggestion.reason && <div className="text-xs zita-ink-muted mt-0.5">{suggestion.reason}</div>}
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={acceptSuggestion}
                  data-testid="save-modal-suggest-accept"
                  className="zita-btn-primary !text-xs !py-1.5 !px-3 inline-flex items-center gap-1"
                >
                  <CheckCircle2 size={11} /> Use this
                </button>
                <button
                  type="button"
                  onClick={rejectSuggestion}
                  className="text-xs zita-ink-muted hover:zita-ink-soft"
                >
                  No thanks
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-5 space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-[0.12em] zita-ink-muted mb-1">
            Existing collection
          </label>
          <select
            value={selected}
            onChange={(e) => { setSelected(e.target.value); if (e.target.value) setNewName(''); }}
            data-testid="save-modal-collection-select"
            className="zita-input w-full px-3 py-2 text-sm"
          >
            <option value="">— Choose or create new below —</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {c.item_count} saved
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.12em] zita-ink-muted mb-1">
            Or new collection name
          </label>
          <input
            type="text"
            value={newName}
            onChange={(e) => { setNewName(e.target.value); if (e.target.value) setSelected(''); }}
            data-testid="save-modal-new-name"
            placeholder="e.g. Cleveland ICU Nurses"
            className="zita-input w-full px-3 py-2 text-sm"
          />
          {newName && collections.length > 0 && (
            <select
              value={parentId || ''}
              onChange={(e) => setParentId(e.target.value || null)}
              className="zita-input w-full px-3 py-2 text-xs mt-2"
              data-testid="save-modal-new-parent"
            >
              <option value="">Top-level folder</option>
              {collections.filter((c) => !c.parent_id).map((c) => (
                <option key={c.id} value={c.id}>Inside: {c.name}</option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.12em] zita-ink-muted mb-1">Tags (comma-separated)</label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            data-testid="save-modal-tags"
            placeholder="urgent, follow-up"
            className="zita-input w-full px-3 py-2 text-sm"
          />
        </div>

        {!bulkMode && (
          <div>
            <label className="block text-xs uppercase tracking-[0.12em] zita-ink-muted mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="save-modal-notes"
              placeholder="Optional context Ari should remember."
              className="zita-input w-full px-3 py-2 text-sm min-h-[70px]"
            />
          </div>
        )}

        {searchTerm && (
          <div className="text-[11px] zita-ink-muted">
            Search context saved: <span className="zita-ink-soft">&ldquo;{searchTerm}&rdquo;</span>
          </div>
        )}
      </div>

      <div className="mt-5 flex items-center gap-2 justify-end">
        <button type="button" onClick={onClose} className="zita-btn-secondary text-sm">Cancel</button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          data-testid="save-modal-submit"
          className="zita-btn-primary text-sm"
        >
          {busy ? 'Saving…' : bulkMode ? `Save ${listings.length}` : 'Save'}
        </button>
      </div>
    </div>
  );

  return (
    <div
      role="dialog"
      aria-label={bulkMode ? 'Save all matches to collection' : 'Save to collection'}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid="save-to-collection-modal"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/45 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md zita-card p-6 sm:p-7 zita-fade-up max-h-[90vh] overflow-y-auto zita-scroll">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-[hsl(var(--zita-silver-soft))] zita-ink-muted"
        >
          <X size={18} />
        </button>
        {loading ? (
          <div className="text-sm zita-ink-muted py-6 text-center">Loading your collections…</div>
        ) : needsAddon ? paywall : form}
      </div>
    </div>
  );
};

export default SaveToCollectionModal;
